/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewTestCampaign.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";

import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import { useParams, useSearchParams } from "react-router-dom";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import LinkTestCasesDialog from "./LinkTestCasesDialog";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";
import ViewTestCampaignLogs from "./ViewTestCampaignLogs";
import RunTestCampaign from "./RunTestCampaign";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";

const CAMPAIGN_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "inactive", label: "Inactive" },
];

export default function ViewTestCampaign() {
  // Dispatch
  const dispatch = useDispatch();
  // Params
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Selectors
  const profileDetails = useSelector((store) => store.profileReducer);

  // Campaign details
  const [campaignDetails, setCampaignDetails] = useState(null);
  // Campaign test cases list
  const [campaignTestCasesList, setCampaignTestCasesList] = useState([]);
  // Tabs
  const [tabValue, setTabValue] = useState(() => {
    const tab = searchParams.get("tab");
    if (tab === "run") return 1;
    if (tab === "log") return 2;
    return 0; // default to VIEW tab
  });
  // Dialogs
  const [openLinkTestCasesDialog, setOpenLinkTestCasesDialog] = useState(false);
  const [openStatusConfirmDialog, setOpenStatusConfirmDialog] = useState(false);
  const [selectedCampaignStatus, setSelectedCampaignStatus] =
    useState("active");
  const currentCampaignStatus = campaignDetails?.status?.toLowerCase();
  const statusChangeOptions = useMemo(
    () =>
      CAMPAIGN_STATUS_OPTIONS.map((option) => ({
        ...option,
        disabled: option.value === currentCampaignStatus,
      })),
    [currentCampaignStatus],
  );
  // Table
  const [tableRowsData, setTableRowsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // recreate state
  const [openRecreateLinkTestCasesDialog, setOpenRecreateLinkTestCasesDialog] =
    useState(false);

  // Handle tab change
  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    const tabNames = ["view", "run", "log"];
    const selectedTab = tabNames[newValue];
    setSearchParams({ tab: selectedTab });
  };

  // Effect to update tab value when URL search params change
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "run") setTabValue(1);
    else if (tab === "log") setTabValue(2);
    else setTabValue(0);
  }, [searchParams]);

  // Handle update campaign
  const handleUpdateCampaign = async ({ campaignId }) => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.update_test_campaign,
        params: {
          campaign_id: campaignId,
          status: selectedCampaignStatus,
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      // Refetch campaign details after update
      await handleGetCampaign({ campaignId });
    }
    setOpenStatusConfirmDialog(false);
  };

  // Handle status change confirmation
  const handleStatusChangeClick = () => {
    setSelectedCampaignStatus("");
    setOpenStatusConfirmDialog(true);
  };

  const handleCancelStatusChange = () => {
    setOpenStatusConfirmDialog(false);
  };

  // Handle get campaign details
  const handleGetCampaign = useCallback(
    async ({ campaignId }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_test_campaign,
          params: { campaign_id: campaignId },
        }),
      );
      if (resp.error) {
        setCampaignDetails(null);
      } else {
        setCampaignDetails(resp?.data?.[0]);
      }
    },
    [dispatch],
  );

  // Handle get campaign linked test cases
  const handleGetCampaignTestCases = useCallback(
    async ({ campaignId }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_test_cases,
          params: {
            campaign_id: campaignId,
            page_number: page,
            page_size: rowsPerPage,
          },
          show_toast: false,
        }),
      );
      if (resp.error) {
        setCampaignTestCasesList([]);
        setTotalCount(0);
      } else {
        setCampaignTestCasesList(resp?.data || []);
        setTotalCount(resp?.total_count || 0);
      }
    },
    [dispatch, page, rowsPerPage],
  );

  // useEffects
  // Get single test campaign details by campaign_id
  useEffect(() => {
    if (id) {
      handleGetCampaign({ campaignId: id });
      handleGetCampaignTestCases({ campaignId: id });
    }
  }, [id, handleGetCampaign, handleGetCampaignTestCases]);

  useEffect(() => {
    // Format table data
    const formattedData =
      campaignTestCasesList?.map((tc, index) => ({
        ...tc,
        id: tc?.test_case_id || (page - 1) * rowsPerPage + index + 1,
        sNo: (page - 1) * rowsPerPage + index + 1,
        created_at: convertUtcToTimeZone(
          tc?.created_at,
          profileDetails?.profile?.profile?.region,
        ),
      })) || [];

    setTableRowsData(formattedData);
  }, [
    campaignTestCasesList,
    page,
    rowsPerPage,
    profileDetails?.profile?.profile?.region,
  ]);

  useEffect(() => {
    if (!openStatusConfirmDialog) return;

    setSelectedCampaignStatus("");
  }, [openStatusConfirmDialog]);

  const COLUMNS = [
    {
      field: "sNo",
      headerName: "S.No",
      flex: 0.3,
      minWidth: 60,
      filterable: false,
      sortable: false,
    },
    {
      field: "manual_test_case_id",
      headerName: "Test cases ID",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "test_case_name",
      headerName: "Test Name",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "test_case_description",
      headerName: "Description",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "created_at",
      headerName: "Created at",
      flex: 1,
      minWidth: 120,
    },
  ];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0.5,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
        mt: -8,
      }}
    >
      {/* Header Section */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        justifyContent="center"
      >
        <Box>
          <Typography variant="body2">
            {campaignDetails?.name || "UI CAMP 1"}{" "}
            <strong>- Test Campaign</strong>
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2">
            {campaignDetails?.project_name || "BMW"} <strong>- Project</strong>
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2">
            {campaignDetails?.variant_name || "petrol"}{" "}
            <strong>- Variant</strong>
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2">
            <IconButton
              size="small"
              onClick={handleStatusChangeClick}
              disabled={
                campaignDetails?.status === "in-progress" || !tableRowsData?.length
              }
              sx={{ color: "#000000" }}
            >
              <BorderColorIcon fontSize="inherit" sx={{ fontSize: 16 }} />
            </IconButton>
            {campaignDetails?.status || "ACTIVE"} <strong>- Status</strong>
          </Typography>
        </Box>
        <Typography variant="body2">
          {convertUtcToTimeZone(
            campaignDetails?.updated_at,
            profileDetails?.profile?.profile?.region,
          ) || "29-07-2025 02:23:09 PM"}{" "}
          <strong>- Updated At</strong> |{" "}
          {convertUtcToTimeZone(
            campaignDetails?.created_at,
            profileDetails?.profile?.profile?.region,
          ) || "29-07-2025 02:22:08 PM"}{" "}
          <strong>- Created At</strong>
        </Typography>
      </Box>

      {/* Tabs */}
      <CustomTabsV2
        value={tabValue}
        onTabChange={handleTabChange}
        tabs={[
          {
            name: "View",
            count: "",
            content: (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {/* Add and Submit Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <AddButtonV2
                    handleOnClick={() => setOpenLinkTestCasesDialog(true)}
                    // disabled={campaignDetails?.status === "active"}
                  />
                </Box>

                {/* DataGrid */}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <CustomDataGridV2
                    rows={tableRowsData}
                    columns={COLUMNS}
                    isMultiLine={true}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handlePageChange={(newPage) => setPage(newPage)}
                    handlePageSizeChange={(newSize) => {
                      setRowsPerPage(newSize);
                      setPage(1);
                    }}
                    totalItems={totalCount}
                  />
                </Box>
              </Box>
            ),
          },
          {
            name: "Run",
            count: "",
            content: (
              <RunTestCampaign
                campaignId={id}
                onCampaignRefresh={handleGetCampaign}
              />
            ),
          },
          {
            name: "Log",
            count: "",
            content: <ViewTestCampaignLogs />,
          },
        ]}
      />

      {/* Link test cases dialog */}
      <LinkTestCasesDialog
        open={openLinkTestCasesDialog}
        onClose={setOpenLinkTestCasesDialog}
        campaignDetails={campaignDetails}
        setCampaignTestCasesList={setCampaignTestCasesList}
        onCampaignRefresh={handleGetCampaign}
      />

      {/* recreate dialog */}
      <LinkTestCasesDialog
        open={openRecreateLinkTestCasesDialog}
        onClose={setOpenRecreateLinkTestCasesDialog}
        campaignDetails={campaignDetails}
        setCampaignTestCasesList={setCampaignTestCasesList}
        onCampaignRefresh={handleGetCampaign}
        isRecreationDialog={true}
      />

      {/* Status confirmation dialog */}
      <CustomDialogBoxV2
        open={openStatusConfirmDialog}
        onClose={handleCancelStatusChange}
        title={"Change Status"}
        successButtonName={"Submit"}
        rejectButtonName={"Cancel"}
        actionButtonStyle={"continuous"}
        onClickSuccess={() => handleUpdateCampaign({ campaignId: id })}
        onClickReject={handleCancelStatusChange}
      >
        <Box
          sx={{
            width: "350px",
            minHeight: "80px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <CustomSelectV2
            label={"Status"}
            optionsList={statusChangeOptions}
            selectedValue={selectedCampaignStatus}
            handleOnChange={(e) => setSelectedCampaignStatus(e.target.value)}
            placeholder={false}
          />
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}
